/**
 * ADMIN ONLY — el formulario que arma el paquete AML de un cliente (Mike, 2-oct,
 * §9.9: "make a form to automatically make these documents"). Resumen + manual
 * de 50 paginas lleno + pagina de firmas, en un PDF. Ver
 * backend/services/aml_paquete.py.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { FileText, Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchJson } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAMPOS = [
  ['company', 'Company legal name', true, '90210 Watches Inc'],
  ['officer', 'AML Compliance Officer', true, 'Full name'],
  ['officer_title', 'Officer title', false, 'Default: AML Compliance Officer'],
  ['address', 'Business address', false, 'Street, city, state, ZIP'],
  ['year', 'Program year', false, 'Default: this year'],
  ['effective', 'Effective date', false, 'Default: the date it is signed'],
  ['owner', 'Owner or senior management (name and title)', false, 'Goes on the signature page'],
];

const VACIO = Object.fromEntries(CAMPOS.map(([k]) => [k, '']));

// El PDF viene como archivo: se baja con el token y se guarda con el nombre que da el servidor.
async function bajar(ruta, token, opciones = {}) {
  const r = await fetch(`${API}${ruta}`, { ...opciones, headers: { ...(opciones.headers || {}), Authorization: `Bearer ${token}` } });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(typeof d.detail === 'string' ? d.detail : (d.detail?.message || `Could not generate the package (${r.status})`));
  }
  const nombre = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'AML_package.pdf';
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = nombre; a.click();
  URL.revokeObjectURL(url);
  return nombre;
}

export default function AdminAmlPage() {
  const { token } = useAuth();
  const [f, setF] = useState(VACIO);
  const [busy, setBusy] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [lista, setLista] = useState([]);

  const cargar = useCallback(() => {
    fetchJson('/admin/aml-packages', { token }).then((d) => setLista(d.records || [])).catch(() => {});
  }, [token]);
  useEffect(() => { cargar(); }, [cargar]);

  const generar = async () => {
    setBusy(true); setAviso(null);
    try {
      const nombre = await bajar('/admin/aml-packages', token, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      setAviso({ ok: true, texto: `${nombre} downloaded. Review it before sending it to the client.` });
      setF(VACIO);
      cargar();
    } catch (e) {
      setAviso({ ok: false, texto: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-aml">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><FileText className="w-6 h-6 text-sky-700" /> AML documents</h1>
        <p className="text-sm text-slate-500">
          The client's AML program in one PDF: a one-page executive summary, the 50-page handbook with the client's
          details filled in (still editable), and the signature page. The client only signs; no financial information is asked.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          {CAMPOS.map(([k, label, obligatorio, ayuda]) => (
            <label key={k} className={`text-sm ${k === 'owner' || k === 'address' ? 'sm:col-span-2' : ''}`}>
              <span className="block text-xs font-medium text-slate-600 mb-1">{label}{obligatorio && ' *'}</span>
              <input className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-900"
                value={f[k]} placeholder={ayuda} maxLength={k === 'year' ? 4 : 150}
                onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`aml-${k}`} />
            </label>
          ))}
        </div>
        <Button onClick={generar} disabled={busy || !f.company.trim() || !f.officer.trim()} data-testid="aml-generate">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" />Generate package (PDF)</>}
        </Button>
        {aviso && (
          <p className={`text-sm ${aviso.ok ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="aml-result">{aviso.texto}</p>
        )}
        <p className="text-xs text-slate-500">
          The summary and the handbook are a policy template, not legal advice or a government certification.
          Check the client's details before it goes out.
        </p>
      </div>

      {lista.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm" data-testid="aml-history">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="text-left px-3 py-2">Company</th><th className="text-left px-3 py-2">Officer</th>
                <th className="text-left px-3 py-2">Year</th><th className="text-left px-3 py-2">Generated</th><th /></tr>
            </thead>
            <tbody>
              {lista.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-3 py-2">{p.data.company}</td>
                  <td className="px-3 py-2">{p.data.officer}</td>
                  <td className="px-3 py-2">{p.data.year}</td>
                  <td className="px-3 py-2 text-slate-500">{new Date(p.created_at).toLocaleString()} · {p.created_by_email}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="text-sky-700 hover:underline" onClick={() => bajar(`/admin/aml-packages/${p.id}/pdf`, token).catch((e) => setAviso({ ok: false, texto: e.message }))}>
                      Download again
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
